import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import graphQLClient from '../../../helpers/graphQLClient';
import {
  ADD_DOMAIN,
  CHECK_HOSTING_V2_DOMAIN,
  REMOVE_DOMAIN,
} from '../../../helpers/graphql/hosting';
import { INK, MUTED } from '../../Landing/ui';

const HAIRLINE =
  'tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-border-solid tw-border-0';

const TONES = {
  amber:
    'tw-bg-[#FBF1DC] tw-text-[#7A5000] dark:tw-bg-[#3A2E14] dark:tw-text-[#E8C27A]',
  teal:
    'tw-bg-[#E0F2EF] tw-text-primary-700 dark:tw-bg-[#12352F] dark:tw-text-primary-300',
  green:
    'tw-bg-[#E3F4EA] tw-text-[#1E6B43] dark:tw-bg-[#173528] dark:tw-text-[#7FD3A4]',
  red:
    'tw-bg-[#FBE6E3] tw-text-[#9B2C1F] dark:tw-bg-[#3A1D19] dark:tw-text-[#F0A397]',
};

// The one thing the owner is waiting on (backend: domainStatusFor). "moving"
// is the switch of a v1 domain, which needs nothing from them.
const STEPS = {
  dns: { label: 'Waiting for DNS', tone: 'amber' },
  verify: { label: 'Prove ownership', tone: 'amber' },
  certificate: { label: 'Securing', tone: 'teal', pulse: true },
  moving: { label: 'Moving over', tone: 'teal', pulse: true },
  live: { label: 'Live', tone: 'green' },
  conflict: { label: 'Points elsewhere', tone: 'red' },
  failed: { label: 'Not connected', tone: 'red' },
  removing: { label: 'Removing', tone: 'teal', pulse: true },
};

const OUTLINE_BUTTON =
  'tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-solid tw-border-primary-600 tw-bg-transparent tw-cursor-pointer tw-px-5 tw-py-2 tw-text-sm tw-font-semibold tw-text-primary-700 dark:tw-text-primary-300 hover:tw-bg-primary-600 hover:tw-text-white tw-transition-colors disabled:tw-opacity-60 disabled:tw-cursor-wait';
const FILLED_BUTTON =
  'tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border-0 tw-cursor-pointer tw-px-5 tw-py-2 tw-text-sm tw-font-semibold tw-bg-primary-600 tw-text-white hover:tw-bg-primary-700 tw-transition-colors disabled:tw-opacity-60 disabled:tw-cursor-wait';
const TEXT_BUTTON = `tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0 tw-text-sm tw-font-semibold tw-underline-offset-4 hover:tw-underline ${MUTED}`;

function Pill({ step }) {
  const meta = STEPS[step];
  if (!meta) return null;
  return (
    <span
      className={`tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-whitespace-nowrap ${
        TONES[meta.tone]
      }`}
    >
      <span
        aria-hidden
        className={`tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-current ${
          meta.pulse ? 'tw-animate-pulse' : ''
        }`}
      />
      {meta.label}
    </span>
  );
}

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="tw-shrink-0 tw-cursor-pointer tw-rounded-full tw-border tw-border-solid tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] tw-bg-transparent tw-px-3 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-primary-700 dark:tw-text-primary-300 hover:tw-border-primary-600 tw-transition-colors"
      onClick={() => {
        if (!navigator.clipboard) return;
        navigator.clipboard
          .writeText(value)
          .then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
          })
          .catch(() => {});
      }}
      aria-label={`Copy ${value}`}
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function RecordsCard({ records }) {
  return (
    <div
      className={`tw-mt-4 tw-overflow-hidden tw-rounded-2xl tw-border ${HAIRLINE}`}
    >
      <div
        className={`tw-hidden sm:tw-grid tw-grid-cols-[9.5rem_6rem_minmax(0,1fr)] tw-gap-4 tw-px-4 tw-py-2 tw-bg-[#F7F3EA] dark:tw-bg-[#101C18] tw-font-mono tw-text-[11px] tw-uppercase tw-tracking-[0.2em] ${MUTED}`}
      >
        <span>Type</span>
        <span>Name</span>
        <span>Value</span>
      </div>
      {records.map(record => (
        <div
          key={record.host}
          className={`tw-grid tw-grid-cols-1 sm:tw-grid-cols-[9.5rem_6rem_minmax(0,1fr)] tw-gap-1 sm:tw-gap-4 tw-items-center tw-px-4 tw-py-3 tw-border-t ${HAIRLINE}`}
        >
          <span className={`${MUTED} tw-text-sm`}>{record.type}</span>
          <span className={`${INK} tw-font-mono tw-text-sm`}>
            {record.name}
            <span className={`${MUTED} sm:tw-hidden`}> · {record.host}</span>
          </span>
          <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-3">
            <span
              className={`${INK} tw-min-w-0 tw-truncate tw-font-mono tw-text-sm`}
            >
              {record.value}
            </span>
            <CopyButton value={record.value} />
            {record.live && (
              <span className="tw-ml-auto tw-text-xs tw-font-semibold tw-text-[#1E6B43] dark:tw-text-[#7FD3A4]">
                Working
              </span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

// The custom-domain row of the Hosting 2.0 panel: connect, follow the
// connection as it happens, remove. The backend loop advances a connecting
// domain on its own; this view only looks again while something moves.
export default function HostingV2Domain({ blog, blogId, refetchHostingPlan }) {
  const { enqueueSnackbar } = useSnackbar();
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const domain = blog.hostingV2Domain;
  const isTrial = blog.plan === 'trial' || blog.plan === 'starter';
  const step =
    domain &&
    domain.movingFromV1 &&
    ['dns', 'certificate'].includes(domain.step)
      ? 'moving'
      : domain && domain.step;

  // A live domain whose www twin still connects keeps being looked at too,
  // or its "joins as soon as" line would outlive the fact.
  const aliasConnecting = step === 'live' && !!(domain && domain.aliasPending);
  useEffect(() => {
    // conflict too: once the owner fixes the record, the backend moves on
    // and the panel must notice without a reload.
    const moving = [
      'dns',
      'verify',
      'certificate',
      'moving',
      'conflict',
      'removing',
    ].includes(step);
    if (!refetchHostingPlan || (!moving && !aliasConnecting)) return undefined;
    const timer = setInterval(
      () => refetchHostingPlan().catch(() => {}),
      step === 'certificate' || step === 'moving'
        ? 6000
        : aliasConnecting
        ? 30000
        : 15000,
    );
    return () => clearInterval(timer);
  }, [step, aliasConnecting, refetchHostingPlan]);

  const refresh = () =>
    refetchHostingPlan ? refetchHostingPlan().catch(() => {}) : null;

  const run = (mutation, variables, key, onSuccess) => {
    setBusy(true);
    graphQLClient(mutation, variables)
      .then(res => {
        const result = (res && res[key]) || {};
        if (result.success) {
          if (onSuccess) onSuccess();
          refresh();
        }
        if (result.message || !result.success)
          enqueueSnackbar(result.message || 'Something went wrong', {
            variant: result.success ? 'success' : 'error',
          });
      })
      .catch(() =>
        enqueueSnackbar('Something went wrong. Please try again.', {
          variant: 'error',
        }),
      )
      .finally(() => setBusy(false));
  };

  const connect = event => {
    event.preventDefault();
    const value = input.trim();
    if (!/^[^\s/]+\.[^\s/]{2,}/.test(value.replace(/^https?:\/\//i, ''))) {
      enqueueSnackbar('Enter a domain like www.yourblog.com', {
        variant: 'error',
      });
      return;
    }
    run(ADD_DOMAIN, { domain: value, blogId }, 'addDomain', () => {
      setEditing(false);
      setInput('');
    });
  };
  const remove = () =>
    run(REMOVE_DOMAIN, { blogId }, 'removeDomain', () =>
      setConfirmRemove(false),
    );
  const checkNow = () =>
    run(CHECK_HOSTING_V2_DOMAIN, { blogId }, 'checkHostingV2Domain');

  let summary;
  let control = null;
  let body = null;

  if (!domain) {
    if (isTrial) {
      summary =
        'Custom domains are part of the paid plans — pick a plan and connect your own.';
    } else if (!editing) {
      summary =
        'Use a domain you own, like yourblog.com. Your blog moves there, and its current address forwards to it.';
      control = (
        <button
          type="button"
          className={OUTLINE_BUTTON}
          onClick={() => setEditing(true)}
        >
          Connect a domain
        </button>
      );
    } else {
      summary =
        'Enter the domain you own. Next, you add one or two DNS records where you bought it.';
      body = (
        <form
          onSubmit={connect}
          className="tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-gap-3"
        >
          <label htmlFor="v2-domain-input" className="tw-sr-only">
            Your domain
          </label>
          <input
            id="v2-domain-input"
            type="text"
            inputMode="url"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoFocus
            placeholder="www.yourblog.com"
            value={input}
            onChange={event => setInput(event.target.value)}
            className={`tw-min-w-0 tw-flex-1 sm:tw-max-w-sm tw-rounded-full tw-border tw-border-solid tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] tw-bg-white dark:tw-bg-[#0F1B17] tw-px-5 tw-py-2.5 tw-font-mono tw-text-sm ${INK} focus:tw-border-primary-600 focus:tw-outline-none`}
          />
          <button type="submit" className={FILLED_BUTTON} disabled={busy}>
            {busy ? 'Connecting…' : 'Connect'}
          </button>
          <button
            type="button"
            className={TEXT_BUTTON}
            onClick={() => {
              setEditing(false);
              setInput('');
            }}
          >
            Cancel
          </button>
        </form>
      );
    }
  } else {
    const host = <strong className={INK}>{domain.hostname}</strong>;
    const records = domain.records || [];
    if (step === 'live') {
      summary = (
        <>
          Your blog is served on {host}.
          {domain.alias && domain.aliasPending && (
            <>
              {' '}
              {domain.alias} joins as soon as its record points to{' '}
              <span className="tw-font-mono">{domain.dnsTarget}</span>.
            </>
          )}
        </>
      );
    } else if (step === 'moving') {
      summary = (
        <>
          {host} is moving to the new hosting. This takes a few minutes and
          needs nothing from you — your DNS stays as it is.
        </>
      );
    } else if (step === 'certificate') {
      summary = (
        <>
          Your DNS is set. The security certificate for {host} is being issued —
          this takes about two minutes.
        </>
      );
    } else if (step === 'conflict') {
      summary = (
        <>
          {host} points to a different TravelFeed blog. Change its record to{' '}
          <span className="tw-font-mono">{domain.dnsTarget}</span>. If you think
          this is a mistake,{' '}
          <Link
            href="/about/contact"
            className="tw-text-primary-700 dark:tw-text-primary-300"
          >
            contact us
          </Link>
          .
        </>
      );
    } else if (step === 'removing') {
      summary = (
        <>
          {host} is being removed. Your blog goes back to{' '}
          <span className="tw-font-mono">{domain.dnsTarget}</span> — this
          finishes by itself in a few minutes.
        </>
      );
    } else if (step === 'verify') {
      summary = (
        <>
          {host} works, but we can’t see where it points — usual for the root of
          a domain on Cloudflare or with an ALIAS record. Add the TXT record
          below to show the domain is yours; it connects right after.
        </>
      );
    } else if (step === 'failed') {
      summary = (
        <>
          We could not connect {host} — either its DNS records never pointed
          here, or another website already uses the name. Remove the domain,
          and connect it again once the records are set.
        </>
      );
    } else {
      summary = (
        <>
          Add {records.length === 1 ? 'this record' : 'these records'} in your
          domain’s DNS settings — usually at the company you bought it from. We
          check automatically and connect {host} as soon as{' '}
          {records.length === 1 ? 'it works' : 'they work'}.
        </>
      );
    }

    const showRecords = ['dns', 'verify', 'conflict'].includes(step);
    const hasApex = records.some(record => record.name === '@');
    const wwwRecord = records.find(record => record.name === 'www');
    body = (
      <>
        {showRecords && <RecordsCard records={records} />}
        {showRecords && (
          <ul
            className={`${MUTED} tw-m-0 tw-mt-3 tw-pl-5 tw-text-sm tw-leading-relaxed`}
          >
            <li>
              A change at your domain provider takes from a few minutes up to a
              few hours to arrive.
            </li>
            {/* Forwarding the root only helps when www is the address we
                serve; with the root as the address, the root must resolve. */}
            {hasApex && !domain.apex && (
              <li>
                No CNAME or ALIAS possible on @ (the root)? Add only the www
                record and forward the root to www at your provider.
              </li>
            )}
            {hasApex && domain.apex && wwwRecord && (
              <li>
                No CNAME or ALIAS possible on @ (the root)? Remove this domain,
                connect {wwwRecord.host} instead, and forward the root to it at
                your provider.
              </li>
            )}
            <li>
              Your DNS is on Cloudflare? Set these records to “DNS only” (grey
              cloud).
            </li>
          </ul>
        )}
        {step !== 'moving' && step !== 'removing' && (
          <div className="tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-gap-4">
            {/* Only where the owner just did something: certificate and move
              finish on their own, and a button there invites mashing. */}
            {(['dns', 'verify', 'conflict'].includes(step) ||
              aliasConnecting) && (
              <button
                type="button"
                className={OUTLINE_BUTTON}
                onClick={checkNow}
                disabled={busy}
              >
                {busy ? 'Checking…' : 'Check now'}
              </button>
            )}
            {confirmRemove ? (
              <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-text-sm">
                <span className={MUTED}>
                  Remove {domain.hostname}? Your blog goes back to{' '}
                  <span className="tw-font-mono">{domain.dnsTarget}</span>.
                </span>
                <button
                  type="button"
                  className={`${TEXT_BUTTON} tw-text-[#9B2C1F] dark:tw-text-[#F0A397]`}
                  onClick={remove}
                  disabled={busy}
                >
                  {busy ? 'Removing…' : 'Remove'}
                </button>
                <button
                  type="button"
                  className={TEXT_BUTTON}
                  onClick={() => setConfirmRemove(false)}
                >
                  Keep it
                </button>
              </span>
            ) : (
              <button
                type="button"
                className={TEXT_BUTTON}
                onClick={() => setConfirmRemove(true)}
              >
                Remove domain
              </button>
            )}
          </div>
        )}
      </>
    );
  }

  // The status pill rides next to the title, never in a right-hand column:
  // on a phone that column would squeeze the explanation to a few words a line.
  return (
    <div>
      <div className="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-start sm:tw-justify-between sm:tw-gap-6">
        <div className="tw-min-w-0">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1">
            <p className={`${INK} tw-m-0 tw-font-semibold`}>Custom domain</p>
            {domain && <Pill step={step} />}
          </div>
          <p
            className={`${MUTED} tw-m-0 tw-mt-1 tw-max-w-xl tw-text-sm tw-leading-relaxed`}
          >
            {summary}
          </p>
        </div>
        {control && <div className="tw-shrink-0">{control}</div>}
      </div>
      {body}
    </div>
  );
}
